import { Play, ThumbsUp } from 'lucide-react';
import { Companion } from '../api/model';

interface HeroSectionProps {
  featuredCompanion: Companion;
  onStartChat: (companion: Companion) => void;
}

function HeroSection({ featuredCompanion, onStartChat }: HeroSectionProps) {
  return (
    <div className="relative mx-2 lg:mx-8 mt-2 lg:mt-8 rounded-xl lg:rounded-2xl overflow-hidden h-[60dvh]">
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-top"
        style={{
          backgroundImage: `url("${featuredCompanion.image}")`,
        }}
      />

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent" />

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col justify-end p-4 lg:p-16">
        <div className="max-w-2xl">
          {/* Mobile Layout */}
          <div className="lg:hidden">
            <h1
              className="text-3xl font-title font-medium text-white mb-3 leading-tight"
              style={{ textShadow: '2px 2px 8px rgba(0,0,0,0.8)' }}
            >
              {featuredCompanion.name}
            </h1>

            <p
              className="text-sm font-body font-light text-white mb-4 leading-relaxed"
              style={{ textShadow: '1px 1px 4px rgba(0,0,0,0.8)' }}
            >
              {featuredCompanion.description}
            </p>

            <div className="mb-4">
              <button
                onClick={() => onStartChat(featuredCompanion)}
                className="w-full flex items-center justify-center space-x-2 bg-kind-purple text-white px-6 py-3 rounded-xl font-bold text-base hover:bg-kind-purple/90 transition-all duration-200"
              >
                <Play className="w-5 h-5 fill-current" />
                <span>Start Your Session</span>
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-2 text-sm text-white/80">
              <span className="bg-kind-teal px-3 py-1 rounded-full text-white font-bold text-xs uppercase">
                {featuredCompanion.category}
              </span>
              <span className="text-sm">{featuredCompanion.tags.join(' • ')}</span>
              <div className="flex items-center space-x-1">
                <ThumbsUp className="w-3 h-3 text-kind-purple" />
                <span className="text-sm">
                  {featuredCompanion.rating} ({featuredCompanion.chat_count} chats)
                </span>
              </div>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden lg:block">
            <h1
              className="text-6xl font-title font-medium text-white mb-6 leading-tight"
              style={{ textShadow: '3px 3px 12px rgba(0,0,0,0.9)' }}
            >
              {featuredCompanion.name}
            </h1>

            <p
              className="text-xl font-body font-light text-white mb-8 leading-relaxed max-w-xl"
              style={{ textShadow: '2px 2px 8px rgba(0,0,0,0.8)' }}
            >
              {featuredCompanion.description}
            </p>

            <div className="mb-8">
              <button
                onClick={() => onStartChat(featuredCompanion)}
                className="flex items-center space-x-3 bg-kind-purple text-white px-8 py-4 rounded-2xl font-bold text-base hover:bg-kind-purple/90 transition-all duration-200 hover-lift"
              >
                <Play className="w-5 h-5 fill-current" />
                <span>Start Your Session</span>
              </button>
            </div>

            <div className="flex items-center space-x-6 text-sm text-white/80">
              <span className="bg-kind-teal px-3 py-2 rounded-full text-white font-bold text-xs uppercase">
                {featuredCompanion.category}
              </span>
              <span className="text-sm">{featuredCompanion.tags.join(' • ')}</span>
              <div className="flex items-center space-x-1">
                <ThumbsUp className="w-3 h-3 text-kind-purple" />
                <span className="text-sm">
                  {featuredCompanion.rating} ({featuredCompanion.chat_count} chats)
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HeroSection;
